import { useCallback, useMemo } from 'react';

import type { UpsellUsageBase } from '@/entities/pricing';

import { useClientStorageItem } from '@/entities/clientStorage';

export const UPSELL_STORAGE_KEY = 'upsellState';

export type UpsellPersistedState = {
  bannerDismissed?: string;
  downloadShown?: boolean;
  purchase?: { at: string; priceId: string; subscriptionId: string };
  usageBase?: UpsellUsageBase;
  usagePeriod?: string;
  welcomeShown?: boolean;
};

type UpsellPatch = Partial<UpsellPersistedState>;

const parse = (value: string | undefined): UpsellPersistedState => {
  if (!value) return {};

  try {
    const parsed: unknown = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? (parsed as UpsellPersistedState) : {};
  } catch {
    return {};
  }
};

export const useUpsellStorage = (enabled: boolean) => {
  const { data, isError, isSuccess, isWriteError, updateItem } = useClientStorageItem(UPSELL_STORAGE_KEY, {
    enabled,
    staleTime: Infinity,
  });
  const state = useMemo(() => parse(data?.value), [data]);

  const update = useCallback(
    (patch: ((current: UpsellPersistedState) => UpsellPatch) | UpsellPatch) =>
      updateItem((current) => {
        const saved = parse(current);

        return JSON.stringify({ ...saved, ...(typeof patch === 'function' ? patch(saved) : patch) });
      }),
    [updateItem],
  );

  return { isError, isReady: enabled && isSuccess, isWriteError, state, update };
};
